import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useUIStore } from '../stores/uiStore';
import { useCommitMutations } from '../hooks/useCommits';
import { getAppUrl } from '../utils/urls';
import CommitEditor from './commit/CommitEditor';
import DeleteCommitDialog from './commit/DeleteCommitDialog';
import LinkedInShareModal from './commit/LinkedInShareModal';
import SettingsModal from './settings/SettingsModal';
import EditProfileModal from './settings/EditProfileModal';
import BranchesModal from './settings/BranchesModal';

export default function GlobalModals() {
  const navigate = useNavigate();
  const location = useLocation();

  // Individual selectors - only re-render when specific modal changes
  const commitEditorOpen = useUIStore((s) => s.commitEditorOpen);
  const commitEditorCommit = useUIStore((s) => s.commitEditorCommit);
  const closeCommitEditor = useUIStore((s) => s.closeCommitEditor);

  const deleteConfirmOpen = useUIStore((s) => s.deleteConfirmOpen);
  const deleteConfirmCommit = useUIStore((s) => s.deleteConfirmCommit);
  const closeDeleteConfirm = useUIStore((s) => s.closeDeleteConfirm);

  const linkedInShareOpen = useUIStore((s) => s.linkedInShareOpen);
  const linkedInShareCommit = useUIStore((s) => s.linkedInShareCommit);
  const closeLinkedInShare = useUIStore((s) => s.closeLinkedInShare);

  const settingsOpen = useUIStore((s) => s.settingsOpen);
  const closeSettings = useUIStore((s) => s.closeSettings);

  const editProfileOpen = useUIStore((s) => s.editProfileOpen);
  const closeEditProfile = useUIStore((s) => s.closeEditProfile);

  const branchesOpen = useUIStore((s) => s.branchesOpen);
  const closeBranches = useUIStore((s) => s.closeBranches);

  const notifyFeedSubscribers = useUIStore((s) => s.notifyFeedSubscribers);
  const notifyProfileSubscribers = useUIStore((s) => s.notifyProfileSubscribers);
  const markCommitDeleted = useUIStore((s) => s.markCommitDeleted);
  const unmarkCommitDeleted = useUIStore((s) => s.unmarkCommitDeleted);

  const { deleteCommit, loading: deleteLoading } = useCommitMutations();

  const handleCommitEditorSuccess = () => {
    closeCommitEditor();
    notifyFeedSubscribers();
    // Also notify profile to update branch commit counts
    notifyProfileSubscribers();
  };

  const handleDeleteConfirm = async () => {
    const commit = deleteConfirmCommit;
    if (!commit) return;

    // Optimistic: hide immediately
    markCommitDeleted(commit.id);
    closeDeleteConfirm();
    toast.success('Commit deleted');

    // Navigate away if on commit detail page
    if (location.pathname.includes(`/commit/${commit.id}`)) {
      navigate(getAppUrl());
    }

    try {
      await deleteCommit(commit.id);
      // Success: notify subscribers to refetch (cleans up any stale data)
      notifyFeedSubscribers();
      // Also notify profile to update branch commit counts
      notifyProfileSubscribers();
    } catch (err) {
      // Rollback: show commit again and refetch
      unmarkCommitDeleted(commit.id);
      notifyFeedSubscribers();
      toast.error('Failed to delete commit');
      console.error(err);
    }
  };

  return (
    <>
      <CommitEditor
        open={commitEditorOpen}
        commit={commitEditorCommit ?? undefined}
        onClose={closeCommitEditor}
        onSuccess={handleCommitEditorSuccess}
      />

      <DeleteCommitDialog
        open={deleteConfirmOpen}
        commit={deleteConfirmCommit}
        onClose={closeDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      <LinkedInShareModal
        open={linkedInShareOpen}
        commit={linkedInShareCommit}
        onClose={closeLinkedInShare}
      />

      <SettingsModal
        open={settingsOpen}
        onClose={closeSettings}
      />

      <EditProfileModal
        open={editProfileOpen}
        onClose={closeEditProfile}
      />

      <BranchesModal
        open={branchesOpen}
        onClose={closeBranches}
      />
    </>
  );
}
